import { z } from 'zod';

/**
 * What a management agreement charges the owner (gitaumoses4/chukua#182, PRD → Owner reporting → Payouts and
 * defaults). A new basis is a migration (a CHECK holds the list) plus a branch in `management-fee.policy.ts`.
 */
export const FEE_BASES = ['net_income_percent'] as const;
export type FeeBasis = (typeof FEE_BASES)[number];

/** What happens to a month that closes below zero: carried into next month, or billed to the owner. */
export const NEGATIVE_BALANCE_TREATMENTS = ['offset_next_month', 'invoice_owner'] as const;
export type NegativeBalanceTreatment = (typeof NEGATIVE_BALANCE_TREATMENTS)[number];

/** Monthly is the only cycle the PRD names; the column is data so a second cycle is a migration, not a rewrite. */
export const PAYOUT_CYCLES = ['monthly'] as const;
export type PayoutCycle = (typeof PAYOUT_CYCLES)[number];

/** Defaults for a deployment that has never set them — the values the migration backfills too. The 15% is still to be confirmed (PRD → Open questions → Management fee). */
export const DEFAULT_FEE_RATE_BPS = 1500;
export const DEFAULT_NEGATIVE_BALANCE_TREATMENT: NegativeBalanceTreatment = 'offset_next_month';

const feeRateBpsSchema = z.number().int().min(0).max(10_000);

/** The terms an agreement carries; any left out take the organization's defaults when the agreement is created. */
export const managementAgreementTermsSchema = z.object({
  feeBasis: z.enum(FEE_BASES).optional(),
  feeRateBps: feeRateBpsSchema.optional(),
  feeInLossMonth: z.boolean().optional(),
  negativeBalanceTreatment: z.enum(NEGATIVE_BALANCE_TREATMENTS).optional(),
  payoutCycle: z.enum(PAYOUT_CYCLES).optional(),
  fixedChargesApply: z.boolean().optional(),
});
export type ManagementAgreementTerms = z.infer<typeof managementAgreementTermsSchema>;

/** `POST /owners/:ownerId/agreements` — `truckId` narrows it to one truck, left out it covers all the owner's trucks. */
export const createManagementAgreementInputSchema = managementAgreementTermsSchema.extend({
  truckId: z.uuid().optional(),
  validFrom: z.iso.date(),
});
export type CreateManagementAgreementInput = z.infer<typeof createManagementAgreementInputSchema>;

/** `POST /agreements/:id/supersede` — changed terms from `effectiveDate`; terms left out carry over from the agreement being replaced. */
export const supersedeManagementAgreementInputSchema = managementAgreementTermsSchema.extend({
  effectiveDate: z.iso.date(),
});
export type SupersedeManagementAgreementInput = z.infer<typeof supersedeManagementAgreementInputSchema>;

/** `GET /trucks/:truckId/agreement?on=` — the date the rule is asked for; today when left out. */
export const truckAgreementQuerySchema = z.object({ on: z.iso.date().optional() });
export type TruckAgreementQuery = z.infer<typeof truckAgreementQuerySchema>;

export const AGREEMENT_STATUSES = ['in_force', 'scheduled', 'past'] as const;
export type AgreementStatus = (typeof AGREEMENT_STATUSES)[number];
